import React, { useState } from 'react';
import { Button, Rate } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
  UserRating as UserRatingInterface,
  UserRatingType,
  PageRoute
} from '../../models/enums';
import { UserRating } from '../../services/api';
import { notification } from '../../services';
import { IndexAvatar } from '../../components';
import { roundToHalf } from '../../utils/ratingHelpers';
import config from 'appConfig';
import styles from './ratingCard.scss';

interface RatingCardProps {
  rating: UserRatingInterface;
  userProfile: {
    username: string;
    avatar?: string;
    userId?: string;
  } | null;
  onAnswerSuccess?: () => void;
  hideAnswerButton?: boolean;
}

const RatingCard: React.FC<RatingCardProps> = ({ rating, userProfile, onAnswerSuccess, hideAnswerButton = false }) => {
  const [showAnswerForm, setShowAnswerForm] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAnswerSubmit = async () => {
    if (!answerText.trim() || submitting) return;

    setSubmitting(true);
    try {
      await UserRating.answer({
        userRatingId: rating.id,
        answerComment: answerText.trim()
      });
      setAnswerText('');
      setShowAnswerForm(false);
      if (onAnswerSuccess) {
        onAnswerSuccess();
      }
    } catch (error) {
      notification.showError('Greška pri slanju odgovora. Molimo pokušajte ponovo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.ratingCard}>
      <div className={styles.ratingHeader}>
        <div className={styles.ratingUser}>
          <Rate disabled value={roundToHalf(rating.overallRating)} allowHalf className={styles.overallStars} />
          <IndexAvatar
            url={rating.avatar}
            className={styles.userAvatar}
            size={24}
          />
          <Link to={`${PageRoute.user}/${rating.username}`} className={styles.usernameLink}>
            <span className={styles.username}>{rating.username}</span>
          </Link>
        </div>
        <span className={styles.date}>
          {moment(rating.createdAt).format(config.dateFormat)}
        </span>
      </div>

      <div className={styles.ratingTitle}>
        {rating.adTitle}
      </div>

      <div className={styles.ratingCriteria}>
        {rating.type === UserRatingType.sellerRating ? (
          <div className={styles.criterionItem}>
            <span className={styles.criterionLabel}>Opis oglasa:</span>
            <span className={styles.starsWrapper}>
              <Rate disabled value={rating.adDescriptionAccuracy} className={styles.criterionStars} />
            </span>
          </div>
        ) : (
          <div className={styles.criterionItem}>
            <span className={styles.criterionLabel}>Plaćanje / zamjena:</span>
            <span className={styles.starsWrapper}>
              <Rate disabled value={rating.paymentAndExchangeAgreement} className={styles.criterionStars} />
            </span>
          </div>
        )}
        <div className={styles.criterionItem}>
          <span className={styles.criterionLabel}>Komunikacija:</span>
          <span className={styles.starsWrapper}>
            <Rate disabled value={rating.communication} className={styles.criterionStars} />
          </span>
        </div>
        <div className={styles.criterionItem}>
          <span className={styles.criterionLabel}>Poštivanje dogovora:</span>
          <span className={styles.starsWrapper}>
            <Rate disabled value={rating.honoringAgreement} className={styles.criterionStars} />
          </span>
        </div>
      </div>

      {rating.comment && (
        <div className={styles.ratingComment}>
          {rating.comment}
        </div>
      )}

      {!hideAnswerButton && rating.canAnswerToComment && !rating.answerComment && !showAnswerForm && (
        <div className={styles.actionButtons}>
          <button
            onClick={() => setShowAnswerForm(true)}
            className={styles.actionButton}
          >
            Odgovorite
          </button>
        </div>
      )}

      {showAnswerForm && (
        <div className={styles.answerFormContainer}>
          <IndexAvatar
            url={userProfile && userProfile.avatar}
            className={styles.answerFormAvatar}
            size={56}
          />
          <div className={styles.answerFormContent}>
            <textarea
              className={styles.answerTextarea}
              placeholder="Upišite tekst odgovora"
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value.slice(0, 600))}
              maxLength={600}
            />
            <div className={styles.answerFormFooter}>
              <span className={styles.characterCount}>{answerText.length} / 600</span>
              <div className={styles.answerFormButtons}>
                <Button
                  onClick={() => {
                    setShowAnswerForm(false);
                    setAnswerText('');
                  }}
                  className={styles.cancelAnswerButton}
                >
                  Odustani
                </Button>
                <Button
                  type="primary"
                  onClick={handleAnswerSubmit}
                  loading={submitting}
                  disabled={!answerText.trim()}
                  className={styles.submitAnswerButton}
                >
                  Pošaljite odgovor
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rating.answerComment && (
        <div className={styles.ratingAnswer}>
          <div className={styles.answerHeader}>
            {userProfile && userProfile.username && (
              <React.Fragment>
                <IndexAvatar
                  url={userProfile.avatar}
                  className={styles.answerAvatar}
                  size={24}
                />
                <Link to={`${PageRoute.user}/${userProfile.username}`} className={styles.answerUsernameLink}>
                  <span className={styles.answerUsername}>{userProfile.username}</span>
                </Link>
              </React.Fragment>
            )}
          </div>
          <div className={styles.answerComment}>
            {rating.answerComment}
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingCard;



// WEBPACK FOOTER //
// ./src/components/RatingCard/index.tsx